import useCampaignHref from '@app/hooks/useCampaignHref'
import urls from '@app/routes/urls'
import { useAppSelector } from '@app/store/hooks'
import { QuizStep } from '@app/store/slices/quiz'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface UseQuizRedirectProps {
  requestedStep: QuizStep
}

function useQuizRedirect({ requestedStep }: UseQuizRedirectProps) {
  const { step, prevStep } = useAppSelector(state => state.quiz)
  const { language } = useAppSelector(state => state.settings)

  const [isRedirecting, setIsRedirecting] = useState(requestedStep !== step)
  const [targetUrl, setTargetUrl] = useState<string | null>(null)

  // Store the current step in a ref so we can compare it in the effect
  // without triggering a re-render
  const stepRef = useRef(step)
  const prevStepRef = useRef(prevStep)
  const navigate = useNavigate()

  // Get correct URLs
  const { href: quizCustomerHref } = useCampaignHref({ requestPath: 'quiz/customer' })
  const { href: quizModelHref } = useCampaignHref({ requestPath: 'quiz/model' })
  const { href: quizPurchaseHref } = useCampaignHref({ requestPath: 'quiz/purchase' })
  const { href: quizValuationHref } = useCampaignHref({ requestPath: 'quiz/valuation' })
  const { href: quizShippingHref } = useCampaignHref({ requestPath: 'quiz/shipping' })
  const { href: quizPaymentHref } = useCampaignHref({ requestPath: 'quiz/payment' })
  const { href: quizCompleteHref } = useCampaignHref({ requestPath: 'quiz/complete' })

  const urlMap = useMemo(
    () => ({
      [QuizStep.NONE]: urls.root,
      [QuizStep.CUSTOMER]: quizCustomerHref,
      [QuizStep.MODEL]: quizModelHref,
      [QuizStep.PURCHASE]: quizPurchaseHref,
      [QuizStep.VALUATION]: quizValuationHref,
      [QuizStep.ADDRESS]: quizShippingHref,
      [QuizStep.PAYMENT]: quizPaymentHref,
      [QuizStep.COMPLETE]: quizCompleteHref,
    }),
    // Update these URLs when the language changes so that we redirect to the correct step in the correct locale
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  )

  useEffect(() => {
    const urlPartOrder = urlMap[stepRef.current]
    const atCorrectStep = requestedStep === prevStepRef.current + 1
    const navBackFromComplete = requestedStep === QuizStep.PAYMENT && prevStepRef.current === QuizStep.PAYMENT

    const modelWasPrevStep = prevStepRef.current === QuizStep.MODEL
    const requestingModel = requestedStep === QuizStep.MODEL

    /**
     * Correct step: Do nothiing
     * Model was the previous step and we are requesting the model: Do nothing
     */
    if (atCorrectStep || (modelWasPrevStep && requestingModel)) {
      setIsRedirecting(false)
      setTargetUrl(null)
      return
    }

    /**
     * Navigating backwards from complete: Go to home
     */
    if (navBackFromComplete) {
      setIsRedirecting(true)
      setTargetUrl(urls.root)
    } else {
      /**
       * Incorrect step: Redirect to correct step
       */
      setIsRedirecting(true)
      setTargetUrl(urlPartOrder)
    }
  }, [requestedStep, urlMap])

  useEffect(() => {
    if (targetUrl) {
      navigate(targetUrl)
    }
  }, [navigate, targetUrl])

  return {
    isRedirecting,
  }
}

export default useQuizRedirect
